import React, { useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Form } from 'react-bootstrap';
import { GripVertical, LayoutList, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { ORDER_TABLE_COLUMNS_DISABLED } from '../../helpers/constants';

const DEFAULT_SWITCH_ON_COLOR = '#FACC15';

const reorder = (list, startIndex, endIndex) => {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const isAlwaysVisible = key => ORDER_TABLE_COLUMNS_DISABLED.includes(key);

const ColumnSwitch = ({ columnKey, label, checked, disabled, onChange }) => (
  <span
    className="flex-shrink-0"
    onClick={e => e.stopPropagation()}
    style={{ position: 'relative', zIndex: 1 }}
  >
    <Form.Check
      type="switch"
      id={`col-${columnKey}`}
      checked={checked}
      disabled={disabled}
      onChange={e => onChange(e.target.checked)}
      className={`mb-0 ${disabled ? 'order-pref-switch--locked' : ''}`}
      style={{ transform: 'scale(0.92)' }}
      title={disabled ? `${label} is always visible` : checked ? `Hide ${label}` : `Show ${label}`}
    />
  </span>
);

const OrderPreference = ({
  open,
  onClose,
  columnDefs,
  tablePrefs,
  onTablePrefsChange,
  switchOnColor = DEFAULT_SWITCH_ON_COLOR
}) => {
  const labelByKey = useMemo(() => Object.fromEntries(columnDefs.map(c => [c.key, c.label])), [columnDefs]);
  const validKeys = useMemo(() => new Set(columnDefs.map(c => c.key)), [columnDefs]);

  const hiddenSet = useMemo(() => new Set(tablePrefs.hiddenColumnKeys || []), [tablePrefs.hiddenColumnKeys]);

  const orderedKeys = useMemo(() => {
    const order = (tablePrefs.columnOrder || []).filter(k => validKeys.has(k));
    const known = new Set(order);
    for (const c of columnDefs) {
      if (!known.has(c.key)) {
        order.push(c.key);
        known.add(c.key);
      }
    }
    return order;
  }, [columnDefs, tablePrefs.columnOrder, validKeys]);

  const visibleColumns = useMemo(() => orderedKeys.filter(k => isAlwaysVisible(k) || !hiddenSet.has(k)), [
    orderedKeys,
    hiddenSet
  ]);

  const hiddenColumns = useMemo(() => orderedKeys.filter(k => !isAlwaysVisible(k) && hiddenSet.has(k)), [
    orderedKeys,
    hiddenSet
  ]);

  const buildColumnOrder = (visible, hidden) => [...visible, ...hidden];

  const handleDragEnd = result => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    const nextVisible = reorder(visibleColumns, source.index, destination.index);
    onTablePrefsChange({
      ...tablePrefs,
      columnOrder: buildColumnOrder(nextVisible, hiddenColumns)
    });
  };

  const activateColumnRow = key => {
    if (isAlwaysVisible(key)) return;
    toggleColumn(key, hiddenSet.has(key));
  };

  const toggleColumn = (key, show) => {
    if (isAlwaysVisible(key)) return;

    if (!show) {
      if (visibleColumns.length <= 1) {
        toast.message('Keep at least one column visible.');
        return;
      }
      if (!hiddenSet.has(key)) {
        const nextVisible = visibleColumns.filter(k => k !== key);
        const nextHidden = [...hiddenColumns, key];
        onTablePrefsChange({
          ...tablePrefs,
          columnOrder: buildColumnOrder(nextVisible, nextHidden),
          hiddenColumnKeys: [...(tablePrefs.hiddenColumnKeys || []), key]
        });
      }
      return;
    }

    if (hiddenSet.has(key)) {
      const nextVisible = [...visibleColumns, key];
      const nextHidden = hiddenColumns.filter(k => k !== key);
      onTablePrefsChange({
        ...tablePrefs,
        columnOrder: buildColumnOrder(nextVisible, nextHidden),
        hiddenColumnKeys: (tablePrefs.hiddenColumnKeys || []).filter(k => k !== key)
      });
    }
  };

  const handleReset = () => {
    const hiddenKeys = columnDefs.filter(c => c.hiddenByDefault).map(c => c.key);
    const defaultPrefs = {
      columnOrder: columnDefs.map(c => c.key),
      hiddenColumnKeys: hiddenKeys
    };

    onTablePrefsChange(defaultPrefs);
    toast.message('Layout reset. Click Save on the toolbar to keep changes.');
  };

  if (!open) return null;

  const rowStyle = (isDragging = false) => ({
    borderColor: isDragging ? '#FACC15' : 'transparent',
    boxShadow: isDragging ? '0 4px 12px rgba(250, 204, 21, 0.15)' : 'none'
  });

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 64,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.25)',
          zIndex: 1038
        }}
      />
      <aside
        className="order-preference-drawer d-flex flex-column bg-white"
        style={{
          position: 'fixed',
          top: 64,
          right: 0,
          width: 'min(400px, 100vw)',
          height: 'calc(100vh - 64px)',
          zIndex: 1039,
          boxShadow: '-8px 0 32px rgba(15, 23, 42, 0.12)',
          borderLeft: '1px solid #e5e7eb',
          ['--order-pref-switch-on']: switchOnColor
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between border-bottom flex-shrink-0"
          style={{ padding: '14px 16px' }}
        >
          <div className="d-flex align-items-center gap-2">
            <LayoutList size={20} className="text-dark" aria-hidden />
            <h2 className="mb-0 text-dark pl-2" style={{ fontSize: '16px', fontWeight: 600 }}>
              Customize View
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="d-flex align-items-center justify-content-center border-0 bg-transparent text-muted"
            style={{ width: 36, height: 36, borderRadius: 8 }}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-grow-1 overflow-auto" style={{ padding: '12px 16px 88px' }}>
          <section>
            <h3
              className="text-muted mb-2"
              style={{
                fontSize: '11px',
                letterSpacing: '0.06em',
                textDecoration: 'underline',
                textUnderlineOffset: '4px'
              }}
            >
              Visible Columns
            </h3>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="visible-columns">
                {provided => (
                  <ul ref={provided.innerRef} {...provided.droppableProps} className="list-unstyled mb-0 p-0 rounded">
                    {visibleColumns.map((key, index) => {
                      const locked = isAlwaysVisible(key);
                      return (
                        <Draggable key={key} draggableId={key} index={index}>
                          {(dragProvided, dragSnapshot) => (
                            <li
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              role={locked ? undefined : 'button'}
                              tabIndex={locked ? undefined : 0}
                              className={`d-flex align-items-center gap-0 bg-white mb-0 py-1 hover-light rounded${
                                locked ? '' : ' order-pref-row--clickable'
                              }`}
                              style={{
                                ...dragProvided.draggableProps.style,
                                ...rowStyle(dragSnapshot.isDragging)
                              }}
                              onClick={() => activateColumnRow(key)}
                              onKeyDown={e => {
                                if (locked) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  activateColumnRow(key);
                                }
                              }}
                            >
                              <span
                                {...dragProvided.dragHandleProps}
                                className="text-muted d-flex flex-shrink-0"
                                style={{ cursor: locked ? 'default' : 'move', width: 24 }}
                                onClick={e => e.stopPropagation()}
                              >
                                <GripVertical size={14} color="#8d959c" />
                              </span>
                              <span className="flex-grow-1 small font-weight-medium text-truncate text-dark">
                                {labelByKey[key]}
                              </span>
                              <ColumnSwitch
                                columnKey={key}
                                label={labelByKey[key]}
                                checked
                                disabled={locked}
                                onChange={show => toggleColumn(key, show)}
                              />
                            </li>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>

            <h3
              className="text-muted mb-2 mt-4"
              style={{
                fontSize: '11px',
                letterSpacing: '0.06em',
                textDecoration: 'underline',
                textUnderlineOffset: '4px'
              }}
            >
              Hidden Columns
            </h3>
            <ul className="list-unstyled mb-0 p-0 rounded">
              {hiddenColumns.map(key => (
                <li
                  key={key}
                  role="button"
                  tabIndex={0}
                  className="d-flex align-items-center gap-0 bg-white mb-0 py-1 hover-light rounded order-pref-row--clickable"
                  onClick={() => activateColumnRow(key)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      activateColumnRow(key);
                    }
                  }}
                >
                  <span className="flex-shrink-0" style={{ width: 4 }} aria-hidden />
                  <span className="flex-grow-1 small font-weight-medium text-truncate text-secondary">
                    {labelByKey[key]}
                  </span>
                  <ColumnSwitch
                    columnKey={key}
                    label={labelByKey[key]}
                    checked={false}
                    disabled={false}
                    onChange={show => toggleColumn(key, show)}
                  />
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div
          className="border-top bg-white flex-shrink-0"
          style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 16px', zIndex: 2 }}
        >
          <button
            type="button"
            className="btn btn-link text-decoration-none px-2 py-1 d-flex align-items-center text-muted hover-border-dark hover-muted"
            onClick={handleReset}
          >
            <RotateCcw size={16} className="mr-1" />
            <span>Reset layout to defaults</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default OrderPreference;
